import { useEffect, useMemo, useRef, useState } from "react";
import "./screens-deal.css";
import type {
  Contribution,
  CostItem,
  DatedFinanceInput,
  DatedMilestone,
  Facility,
  Parcel,
  RentLine,
} from "./dated-finance-types";
import { computeDatedFinance } from "./dated-finance";
import {
  computeDatedOwnership,
  type DatedFlow,
  type OwnershipInput,
} from "./dated-ownership";
import { previewDatedMigration } from "./deal-migration";
import { resolveScenarioOwnershipInput } from "./scenario-ownership";
import {
  buildDealReport,
  exportDealCsv,
  exportDealExcel,
  exportDealPdf,
} from "./deal-report";
import { useScenario } from "./state";
import type { Scenario } from "./types";

const today = () => new Date().toISOString().slice(0, 10);
const id = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const number = (value: string) => (value === "" ? Number.NaN : Number(value));
const money = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value)
    ? "Unknown"
    : new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(value);
const pct = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value)
    ? "Unknown"
    : `${(value * 100).toFixed(1)}%`;

const humanOwnershipDiagnostic = (item: string): string => {
  if (item.startsWith("terminalValue unknown"))
    return "Terminal value unknown: settled ownership advantage is suppressed; a missing value is not treated as zero.";
  if (item.startsWith("sellingCosts unknown"))
    return "Selling costs unknown: settled ownership advantage is suppressed; missing costs are not treated as zero.";
  if (item.startsWith("ownerLifecycle not provided"))
    return "Owner lifecycle not provided: recurring ownership costs are unknown; settled advantage is suppressed.";
  return item;
};
function Panel({
  title,
  action,
  children,
  open = false,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
}) {
  return (
    <section className="panel">
      <details open={open}>
        <summary>
          <h3>{title}</h3>
        </summary>
        <div className="panel-heading">{action}</div>
        {children}
      </details>
    </section>
  );
}
function Add({
  onClick,
  children,
  disabled = false,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="btn" onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}
function Remove({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn" onClick={onClick}>
      Remove
    </button>
  );
}
function Num({
  label,
  value,
  onChange,
  percent = false,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (value: number) => void;
  percent?: boolean;
}) {
  return (
    <label>
      {label}
      <input
        aria-label={label}
        type="number"
        step="0.01"
        value={value == null ? "" : percent ? value * 100 : value}
        onChange={(e) =>
          onChange(
            percent ? number(e.target.value) / 100 : number(e.target.value),
          )
        }
      />
    </label>
  );
}
function Issues({
  items,
  collapsible = false,
}: {
  items: string[];
  collapsible?: boolean;
}) {
  const list = (
    <ul>
      {items.map((x, i) => (
        <li key={`${x}-${i}`}>{x}</li>
      ))}
    </ul>
  );
  return items.length ? (
    collapsible ? (
      <details className="callout warning">
        <summary>
          <strong>Review required ({items.length})</strong>
          <span className="muted">
            Returns unavailable until pending inputs are resolved.
          </span>
        </summary>
        {list}
      </details>
    ) : (
      <div className="callout warning" role="status">
        <strong>Review required</strong>
        {list}
      </div>
    )
  ) : null;
}
function Lines({
  label,
  lines,
  onChange,
}: {
  label: string;
  lines: DatedFlow[];
  onChange: (next: DatedFlow[]) => void;
}) {
  return (
    <div className="well">
      <div className="panel-heading">
        <h4>{label}</h4>
        <Add onClick={() => onChange([...lines, { date: today(), amount: 0 }])}>
          Add row
        </Add>
      </div>
      {lines.map((line, index) => (
        <div className="form-grid" key={index}>
          <label>
            Date
            <input
              type="date"
              value={line.date}
              onChange={(e) =>
                onChange(
                  lines.map((x, i) =>
                    i === index ? { ...x, date: e.target.value } : x,
                  ),
                )
              }
            />
          </label>
          <Num
            label="Amount"
            value={line.amount}
            onChange={(amount) =>
              onChange(
                lines.map((x, i) => (i === index ? { ...x, amount } : x)),
              )
            }
          />
          <label>
            Label
            <input
              value={line.label ?? ""}
              onChange={(e) =>
                onChange(
                  lines.map((x, i) =>
                    i === index
                      ? { ...x, label: e.target.value || undefined }
                      : x,
                  ),
                )
              }
            />
          </label>
          <Remove
            onClick={() => onChange(lines.filter((_, i) => i !== index))}
          />
        </div>
      ))}
    </div>
  );
}
function ContributionLines({
  lines,
  onChange,
}: {
  lines: Contribution[];
  onChange: (next: Contribution[]) => void;
}) {
  return (
    <div className="well">
      <div className="panel-heading">
        <h4>Contributions</h4>
        <Add
          onClick={() =>
            onChange([
              ...lines,
              { date: today(), amount: 0, purpose: "funding" },
            ])
          }
        >
          Add row
        </Add>
      </div>
      {lines.map((row, i) => (
        <div className="form-grid" key={i}>
          <label>
            Date
            <input
              type="date"
              value={row.date}
              onChange={(e) =>
                onChange(
                  lines.map((x, j) =>
                    j === i ? { ...x, date: e.target.value } : x,
                  ),
                )
              }
            />
          </label>
          <Num
            label="Amount"
            value={row.amount}
            onChange={(amount) =>
              onChange(lines.map((x, j) => (j === i ? { ...x, amount } : x)))
            }
          />
          <label>
            Purpose
            <select
              value={row.purpose ?? ""}
              onChange={(e) =>
                onChange(
                  lines.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          purpose: e.target.value
                            ? (e.target.value as Contribution["purpose"])
                            : undefined,
                        }
                      : x,
                  ),
                )
              }
            >
              <option value="" disabled>
                Choose purpose
              </option>
              <option value="funding">Funding</option>
              <option value="rent-credit">Rent credit</option>
              <option value="tenant-owned-work">Tenant-owned work</option>
            </select>
          </label>
          <label>
            Party
            <input
              value={row.party ?? ""}
              onChange={(e) =>
                onChange(
                  lines.map((x, j) =>
                    j === i ? { ...x, party: e.target.value || undefined } : x,
                  ),
                )
              }
            />
          </label>
          <label>
            Source
            <input
              value={row.source ?? ""}
              onChange={(e) =>
                onChange(
                  lines.map((x, j) =>
                    j === i ? { ...x, source: e.target.value || undefined } : x,
                  ),
                )
              }
            />
          </label>
          <label>
            Specified purpose
            <input
              value={row.specifiedPurpose ?? ""}
              onChange={(e) =>
                onChange(
                  lines.map((x, j) =>
                    j === i
                      ? { ...x, specifiedPurpose: e.target.value || undefined }
                      : x,
                  ),
                )
              }
            />
          </label>
          <Remove onClick={() => onChange(lines.filter((_, j) => j !== i))} />
        </div>
      ))}
    </div>
  );
}

export function DealWorkspace() {
  const {
    active,
    createDatedScenario,
    createRevHDraft,
    updateDealScenario,
    resolveDealInput,
  } = useScenario();
  const [anchorDate, setAnchorDate] = useState(today);
  const preview = useMemo(
    () => previewDatedMigration(active, anchorDate),
    [active, anchorDate],
  );
  if (active.calculationMethod !== "dated-v2" || !active.datedFinance)
    return (
      <main
        className="page dated-deal-workspace"
        aria-label="Dated deal workspace"
      >
        <div className="page-header">
          <div>
            <p className="eyebrow">Dated v2 workspace</p>
            <h2>Create a dated scenario</h2>
            <p className="muted">
              The source scenario is retained. Pre-finance source costs are
              copied with assumed initial dates; finance and ownership terms
              require review.
            </p>
          </div>
        </div>
        <Panel
          title="Legacy clone preview"
          action={
            <Add onClick={() => createDatedScenario(active.id, anchorDate)}>
              Create dated clone
            </Add>
          }
        >
          <div className="form-grid">
            <label>
              Input anchor date
              <input
                aria-label="Input anchor date"
                type="date"
                value={anchorDate}
                onChange={(e) => setAnchorDate(e.target.value)}
              />
            </label>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Initial payment date</th>
                </tr>
              </thead>
              <tbody>
                {preview.sourcedCostAmounts.map((x) => (
                  <tr key={x.category}>
                    <td>{x.category}</td>
                    <td>{money(x.amount)}</td>
                    <td>{x.source}</td>
                    <td>{anchorDate} (assumed)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Issues items={[...preview.assumedDates, ...preview.unresolved]} />
        </Panel>
        <Panel
          title="Waterloo Scenario 2 · RevH draft"
          action={<Add onClick={createRevHDraft}>Create RevH draft</Add>}
        >
          <p>
            Uses the controlled RevH canon: nine dated cost rows including six
            source construction packages and a 5% adopted builder-margin loading
            (the historical 4% source rate was an error), $109m known land
            amount, PC 1 Oct 2028, assumed opening 1 Jan 2029 from month-level
            source precision, and annual quoted rent $13,454,440. ESR
            settlement, parcel/deposit terms, funding, allowances, rent
            commencement and contribution timing remain unresolved.
          </p>
        </Panel>
      </main>
    );
  if (!active.datedOwnership)
    return (
      <main
        className="dated-deal-workspace page"
        aria-label="Dated deal workspace"
      >
        <div className="page-header">
          <div>
            <p className="eyebrow">Dated v2 · ownership inputs pending</p>
            <h2>Set up ownership comparison</h2>
            <p className="muted">
              Your dated finance ledger is retained. Add an explicit ownership
              input set to assess lease versus own; terminal value, selling
              costs and lifecycle costs will remain unknown until entered.
            </p>
          </div>
        </div>
        <Panel
          title="Ownership comparison"
          open
          action={
            <Add
              onClick={() =>
                updateDealScenario(active.id, {
                  datedOwnership: {
                    valuationDate: active.datedFinance!.startDate,
                    endDate: active.datedFinance!.endDate,
                    annualDiscountRate:
                      active.assumptions.schoolWacc ??
                      active.assumptions.discountRate,
                    leasePaymentSource: "manual",
                    leasePayments: [],
                    ownerAcquisitionAndBuildCosts: [],
                    terminalValue: null,
                    sellingCosts: null,
                    ownerLifecycle: null,
                  },
                })
              }
            >
              Set up ownership comparison
            </Add>
          }
        >
          <p className="muted">
            This action creates only a dated ownership input shell. It does not
            change the finance ledger or fill unknown ownership assumptions.
          </p>
        </Panel>
      </main>
    );
  return (
    <Editor
      scenario={active}
      finance={active.datedFinance}
      ownership={active.datedOwnership}
      unresolved={active.unresolvedInputNotes ?? []}
      resolveDealInput={resolveDealInput}
      createDatedScenario={createDatedScenario}
      patch={(patch) =>
        updateDealScenario(active.id, {
          calculationMethod: "dated-v2",
          ...patch,
        })
      }
    />
  );
}

function Editor({
  scenario,
  finance,
  ownership,
  unresolved,
  resolveDealInput,
  createDatedScenario,
  patch,
}: {
  scenario: Scenario;
  finance: DatedFinanceInput;
  ownership: OwnershipInput;
  unresolved: string[];
  resolveDealInput: ReturnType<typeof useScenario>["resolveDealInput"];
  createDatedScenario: ReturnType<typeof useScenario>["createDatedScenario"];
  patch: (patch: {
    datedFinance?: DatedFinanceInput;
    datedOwnership?: OwnershipInput;
  }) => void;
}) {
  const financeRun = useMemo(() => {
    try {
      return { value: computeDatedFinance(finance), error: "" };
    } catch (e) {
      return {
        value: null,
        error:
          e instanceof Error
            ? e.message
            : "Invalid dated finance configuration.",
      };
    }
  }, [finance]);
  const derivedLease =
    ownership.leasePaymentSource !== "manual" &&
    ownership.leasePayments.length === 0;
  const ownershipInput = useMemo<OwnershipInput>(
    () =>
      resolveScenarioOwnershipInput(scenario, financeRun.value ?? undefined) ??
      ownership,
    [financeRun.value, ownership, scenario],
  );
  const ownershipRun = useMemo(() => {
    try {
      return { value: computeDatedOwnership(ownershipInput), error: "" };
    } catch (e) {
      return {
        value: null,
        error:
          e instanceof Error ? e.message : "Invalid ownership configuration.",
      };
    }
  }, [ownershipInput]);
  const f = (next: Partial<DatedFinanceInput>) =>
    patch({ datedFinance: { ...finance, ...next } });
  const o = (next: Partial<OwnershipInput>) =>
    patch({ datedOwnership: { ...ownership, ...next } });
  const result = financeRun.value,
    own = ownershipRun.value;
  const unscheduledCostTotal = (finance.costs ?? [])
    .filter(
      (cost) =>
        !cost.date && !cost.milestone && !cost.profile && !cost.recurring,
    )
    .reduce((total, cost) => total + (cost.amount ?? 0), 0);
  const operatorPeakDeficit = (() => {
    let cumulative = 0;
    let minimum = 0;
    for (const flow of result?.cashflows ?? []) {
      cumulative += flow.operator;
      minimum = Math.min(minimum, cumulative);
    }
    return Math.max(0, -minimum);
  })();
  const financeComplete = result?.complete === true && unresolved.length === 0;
  const report = useMemo(
    () => buildDealReport([scenario], { baselineId: scenario.id }),
    [scenario],
  );
  const ownershipDiagnostics = useMemo(() => {
    const all = [
      ...(own?.errors ?? []),
      ...(own?.issues ?? []),
      ...(own?.suppressionReasons ?? []),
    ];
    const readableList = all.filter(Boolean).map(humanOwnershipDiagnostic);
    return Array.from(new Set(readableList));
  }, [own?.errors, own?.issues, own?.suppressionReasons]);

  const [exportError, setExportError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const exportCleanups = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const [url, timer] of exportCleanups.current) {
        clearTimeout(timer);
        URL.revokeObjectURL(url);
      }
      exportCleanups.current.clear();
    };
  }, []);
  const setExportStatus = (error: string | null) => {
    if (mountedRef.current) setExportError(error);
  };
  const handleDownloadCsv = () => {
    try {
      const csvContent = exportDealCsv(report);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deal-report-${scenario.id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      const timer = setTimeout(() => {
        URL.revokeObjectURL(url);
        exportCleanups.current.delete(url);
      }, 10_000);
      exportCleanups.current.set(url, timer);
      setExportStatus(null);
    } catch (err) {
      setExportStatus(
        err instanceof Error ? err.message : "Failed to export CSV",
      );
    }
  };
  const handleDownloadExcel = async () => {
    try {
      await exportDealExcel(report);
      setExportStatus(null);
    } catch (err) {
      setExportStatus(
        err instanceof Error ? err.message : "Failed to export Excel",
      );
    }
  };
  const handleDownloadPdf = async () => {
    try {
      await exportDealPdf(report);
      setExportStatus(null);
    } catch (err) {
      setExportStatus(
        err instanceof Error ? err.message : "Failed to export PDF",
      );
    }
  };

  const issueItems = [
    ...new Set(
      [
        ...unresolved,
        financeRun.error,
        ownershipRun.error,
        ...(result?.issues.map((x) => x.message) ?? []),
        ...(result?.unresolvedInputs ?? []),
      ].filter(Boolean),
    ),
  ];
  const [resolutionNotes, setResolutionNotes] = useState<
    Record<string, string>
  >({});
  return (
    <main
      className="dated-deal-workspace page"
      aria-label="Dated deal workspace"
    >
      <div className="page-header">
        <div>
          <p className="eyebrow">Dated v2 · cash ledger basis</p>
          <h2>Deal workspace</h2>
          <p className="muted">
            These results are calculated only from dated cashflows. Legacy
            formula sheets and exports are unavailable while this scenario is
            active.
          </p>
        </div>
        <div className="row gap-2">
          <Add
            onClick={() => createDatedScenario(scenario.id, finance.startDate)}
          >
            Duplicate dated scenario
          </Add>
        </div>
      </div>
      <Issues items={issueItems} collapsible />
      <Panel title="Dated financial summary" open>
        <div className="metric-grid">
          <div>
            <span>Scheduled project costs</span>
            <strong>
              {money(
                Object.values(result?.costBuckets ?? {}).reduce(
                  (a, b) => a + b,
                  0,
                ),
              )}
            </strong>
          </div>
          <div>
            <span>Unscheduled cost total</span>
            <strong>{money(unscheduledCostTotal)}</strong>
          </div>
          <div>
            <span>Interest</span>
            <strong>
              {financeComplete ? money(result?.totals.interest) : "Unknown"}
            </strong>
          </div>
          <div>
            <span>Fees</span>
            <strong>
              {financeComplete ? money(result?.totals.fees) : "Unknown"}
            </strong>
          </div>
          <div>
            <span>Peak debt</span>
            <strong>
              {financeComplete ? money(result?.peakDebt) : "Unknown"}
            </strong>
          </div>
          <div>
            <span>Equity required</span>
            <strong>
              {financeComplete ? money(result?.equityRequired) : "Unknown"}
            </strong>
          </div>
          <div>
            <span>Project IRR</span>
            <strong>{pct(result?.projectIRR)}</strong>
          </div>
          <div>
            <span>Operator funding requirement</span>
            <strong>
              {financeComplete ? money(operatorPeakDeficit) : "Unknown"}
            </strong>
          </div>
          <div>
            <span>Ownership advantage</span>
            <strong>{money(own?.advantage)}</strong>
          </div>
        </div>
        <p className="muted">
          Returns stay suppressed until their dated input set is complete.
        </p>
        {!financeComplete && (
          <p className="muted">
            Incomplete finance or funding figures are unavailable; the ledgers
            retain entered diagnostics.
          </p>
        )}
      </Panel>
      <Panel title="Dated exports" open>
        <p className="muted">
          Exports are immutable dated-ledger snapshots; legacy workbook formulas
          are not used.
        </p>
        {exportError && (
          <div className="callout warning" role="status">
            <strong>Export error:</strong> {exportError}
          </div>
        )}
        <div className="row gap-2">
          <Add onClick={handleDownloadCsv}>Download CSV</Add>
          <Add onClick={() => void handleDownloadExcel()}>Download Excel</Add>
          <Add onClick={() => void handleDownloadPdf()}>Download PDF</Add>
        </div>
      </Panel>
      <Panel title="Ownership diagnostics" open>
        {ownershipDiagnostics.length ? (
          <Issues items={ownershipDiagnostics} />
        ) : (
          <p className="muted">
            No ownership diagnostics are currently reported.
          </p>
        )}
      </Panel>
      {unresolved.length > 0 && (
        <Panel title="Reviewed input resolutions">
          <p className="muted">
            Record why each assumption is now reviewed before removing it from
            the dated return gate. This does not settle independent validation
            errors.
          </p>
          {unresolved.map((item) => (
            <div className="form-grid" key={item}>
              <label>
                Unresolved input
                <input readOnly value={item} />
              </label>
              <label>
                Resolution note
                <textarea
                  aria-label={`Resolution note for ${item}`}
                  value={resolutionNotes[item] ?? ""}
                  onChange={(event) =>
                    setResolutionNotes((notes) => ({
                      ...notes,
                      [item]: event.target.value,
                    }))
                  }
                />
              </label>
              <Add
                disabled={!(resolutionNotes[item] ?? "").trim()}
                onClick={() => {
                  resolveDealInput(
                    scenario.id,
                    item,
                    resolutionNotes[item] ?? "",
                  );
                  setResolutionNotes((notes) => ({ ...notes, [item]: "" }));
                }}
              >
                Record review
              </Add>
            </div>
          ))}
        </Panel>
      )}
      <Programme finance={finance} patch={f} />
      <Parcels finance={finance} patch={f} />
      <Costs finance={finance} patch={f} />
      <Facilities finance={finance} patch={f} />
      <Operating finance={finance} patch={f} />
      <Ownership
        ownership={ownership}
        landlordRent={[...(finance.landlordRent ?? [])]}
        derivedLease={derivedLease}
        patch={o}
      />
      <Ledger
        title="Dated project ledger"
        rows={
          result?.ledger.map((x) => [
            x.date,
            x.category,
            x.description,
            money(x.project),
            money(x.operator),
            money(x.amount),
          ]) ?? []
        }
        headers={[
          "Date",
          "Category",
          "Description",
          "Project",
          "Operator",
          "Amount",
        ]}
      />
      <Ledger
        title="Ownership lease-versus-own ledger"
        rows={
          own?.datedFlows.map((x) => [
            x.date,
            x.series,
            x.label,
            money(x.amount),
            money(x.pv),
            x.withinHorizon ? "Yes" : "No",
          ]) ?? []
        }
        headers={["Date", "Series", "Label", "Amount", "PV", "In horizon"]}
      />
    </main>
  );
}
function Ledger({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <Panel title={title}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {headers.map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((value, j) => (
                  <td key={j}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
function Programme({
  finance,
  patch,
}: {
  finance: DatedFinanceInput;
  patch: (x: Partial<DatedFinanceInput>) => void;
}) {
  const rows = finance.milestones ?? [];
  const update = (i: number, x: DatedMilestone) =>
    patch({ milestones: rows.map((row, j) => (i === j ? x : row)) });
  return (
    <Panel
      title="Programme and milestones"
      action={
        <Add
          onClick={() =>
            patch({ milestones: [...rows, { id: id("milestone") }] })
          }
        >
          Add milestone
        </Add>
      }
    >
      <div className="form-grid">
        <label>
          Start date
          <input
            type="date"
            value={finance.startDate}
            onChange={(e) => patch({ startDate: e.target.value })}
          />
        </label>
        <label>
          End date
          <input
            type="date"
            value={finance.endDate}
            onChange={(e) => patch({ endDate: e.target.value })}
          />
        </label>
        <Num
          label="Project discount rate (%)"
          value={finance.discountRate ?? 0.1}
          percent
          onChange={(discountRate) => patch({ discountRate })}
        />
      </div>
      {rows.map((row, i) => (
        <div className="form-grid" key={row.id}>
          <label>
            Milestone ID
            <input
              value={row.id}
              onChange={(e) => update(i, { ...row, id: e.target.value })}
            />
          </label>
          <label>
            Fixed date
            <input
              type="date"
              value={row.date ?? ""}
              onChange={(e) =>
                update(i, { ...row, date: e.target.value || undefined })
              }
            />
          </label>
          <label>
            Predecessor ID
            <input
              value={row.predecessors?.[0]?.id ?? ""}
              onChange={(e) =>
                update(i, {
                  ...row,
                  predecessors: e.target.value
                    ? [
                        {
                          id: e.target.value,
                          days: row.predecessors?.[0]?.days ?? 0,
                        },
                      ]
                    : [],
                })
              }
            />
          </label>
          <Num
            label="Dependency offset (days)"
            value={row.predecessors?.[0]?.days ?? 0}
            onChange={(days) =>
              update(i, {
                ...row,
                predecessors: row.predecessors?.[0]
                  ? [{ ...row.predecessors[0], days }]
                  : [],
              })
            }
          />
          <Remove
            onClick={() =>
              patch({ milestones: rows.filter((_, j) => j !== i) })
            }
          />
        </div>
      ))}
    </Panel>
  );
}
function Parcels({
  finance,
  patch,
}: {
  finance: DatedFinanceInput;
  patch: (x: Partial<DatedFinanceInput>) => void;
}) {
  const rows = finance.parcels ?? [];
  const update = (i: number, x: Parcel) =>
    patch({ parcels: rows.map((row, j) => (i === j ? x : row)) });
  return (
    <Panel
      title="Parcels"
      action={
        <Add
          onClick={() =>
            patch({ parcels: [...rows, { id: id("parcel"), price: 0 }] })
          }
        >
          Add parcel
        </Add>
      }
    >
      <>
        {rows.map((row, i) => (
          <div className="form-grid" key={row.id ?? i}>
            <label>
              Parcel ID
              <input
                value={row.id ?? ""}
                onChange={(e) => update(i, { ...row, id: e.target.value })}
              />
            </label>
            <Num
              label="Purchase price"
              value={row.price}
              onChange={(price) => update(i, { ...row, price })}
            />
            <Num
              label="Deposit"
              value={row.deposit}
              onChange={(deposit) => update(i, { ...row, deposit })}
            />
            <label>
              Exchange date
              <input
                type="date"
                value={row.exchange ?? ""}
                onChange={(e) =>
                  update(i, { ...row, exchange: e.target.value || undefined })
                }
              />
            </label>
            <label>
              Settlement date
              <input
                type="date"
                value={row.settlement ?? ""}
                onChange={(e) =>
                  update(i, { ...row, settlement: e.target.value || undefined })
                }
              />
            </label>
            <Remove
              onClick={() => patch({ parcels: rows.filter((_, j) => j !== i) })}
            />
          </div>
        ))}
      </>
    </Panel>
  );
}
function Costs({
  finance,
  patch,
}: {
  finance: DatedFinanceInput;
  patch: (x: Partial<DatedFinanceInput>) => void;
}) {
  const rows = finance.costs ?? [];
  const update = (i: number, x: CostItem) =>
    patch({ costs: rows.map((row, j) => (i === j ? x : row)) });
  return (
    <Panel
      title="Costs, dates and profiles"
      action={
        <Add
          onClick={() =>
            patch({
              costs: [
                ...rows,
                {
                  id: id("cost"),
                  category: "unclassified",
                  amount: 0,
                  date: finance.startDate,
                },
              ],
            })
          }
        >
          Add cost
        </Add>
      }
    >
      <>
        {rows.map((row, i) => {
          type CostMode = "date" | "milestone" | "profile" | "recurring";
          const currentMode: CostMode =
            row.recurring != null
              ? "recurring"
              : row.profile != null && Object.keys(row.profile).length > 0
                ? "profile"
                : row.milestone != null && row.milestone !== ""
                  ? "milestone"
                  : "date";

          const weightSum = Object.values(row.profile ?? {}).reduce(
            (acc, w) => acc + (Number.isFinite(w) ? w : 0),
            0,
          );
          const isWeightSumOne = Math.abs(weightSum - 1) < 1e-8;

          const handleModeChange = (mode: CostMode) => {
            if (mode === currentMode) return;
            const next: CostItem = {
              ...row,
              date: undefined,
              milestone: undefined,
              profile: undefined,
              recurring: undefined,
            };
            if (mode === "date") {
              next.date = row.date || finance.startDate;
            } else if (mode === "milestone") {
              next.milestone =
                row.milestone || (finance.milestones?.[0]?.id ?? "milestone-1");
            } else if (mode === "profile") {
              next.profile =
                row.profile && Object.keys(row.profile).length > 0
                  ? row.profile
                  : { [finance.milestones?.[0]?.id ?? "milestone-1"]: 1 };
            } else if (mode === "recurring") {
              next.recurring = row.recurring ?? {
                start: finance.startDate,
                end: finance.endDate,
                amount: 0,
                frequency: "monthly",
              };
            }
            update(i, next);
          };

          return (
            <div className="form-grid cost-item-card" key={row.id ?? i}>
              <label>
                Category
                <input
                  value={row.category}
                  onChange={(e) =>
                    update(i, { ...row, category: e.target.value })
                  }
                />
              </label>
              <Num
                label="Cost amount"
                value={row.amount}
                onChange={(amount) => update(i, { ...row, amount })}
              />
              <label>
                Cost schedule mode
                <select
                  aria-label="Cost schedule mode"
                  value={currentMode}
                  onChange={(e) => handleModeChange(e.target.value as CostMode)}
                >
                  <option value="date">Fixed date</option>
                  <option value="milestone">Milestone linked</option>
                  <option value="profile">Profile weights</option>
                  <option value="recurring">Recurring series</option>
                </select>
              </label>
              {currentMode === "date" && (
                <label>
                  Payment date
                  <input
                    aria-label="Payment date"
                    type="date"
                    value={row.date ?? ""}
                    onChange={(e) =>
                      update(i, { ...row, date: e.target.value || undefined })
                    }
                  />
                </label>
              )}
              {currentMode === "milestone" && (
                <label>
                  Milestone ID
                  <input
                    aria-label="Milestone ID"
                    value={row.milestone ?? ""}
                    onChange={(e) =>
                      update(i, {
                        ...row,
                        milestone: e.target.value || undefined,
                      })
                    }
                  />
                </label>
              )}
              {currentMode === "profile" && (
                <div className="well profile-editor">
                  <div className="panel-heading">
                    <strong>Profile weights</strong>
                    <span
                      className={`weight-indicator ${
                        isWeightSumOne ? "weight-valid" : "weight-invalid"
                      }`}
                    >
                      Sum: {(weightSum * 100).toFixed(1)}%{" "}
                      {isWeightSumOne ? "(100%)" : "(must equal 100%)"}
                    </span>
                  </div>
                  {Object.entries(row.profile ?? {}).map(([key, weight]) => (
                    <div className="row gap-2" key={key}>
                      <label>
                        Profile milestone
                        <input
                          aria-label={`Profile milestone ${key}`}
                          value={key}
                          onChange={(e) => {
                            const newKey = e.target.value;
                            const profile: Record<string, number> = {};
                            for (const [k, v] of Object.entries(
                              row.profile ?? {},
                            )) {
                              if (k === key) {
                                profile[newKey] = v;
                              } else {
                                profile[k] = v;
                              }
                            }
                            update(i, { ...row, profile });
                          }}
                        />
                      </label>
                      <Num
                        label={`Weight for ${key}`}
                        value={weight}
                        onChange={(next) =>
                          update(i, {
                            ...row,
                            profile: { ...(row.profile ?? {}), [key]: next },
                          })
                        }
                      />
                      <Remove
                        onClick={() => {
                          const profile = { ...(row.profile ?? {}) };
                          delete profile[key];
                          update(i, {
                            ...row,
                            profile: Object.keys(profile).length
                              ? profile
                              : undefined,
                          });
                        }}
                      />
                    </div>
                  ))}
                  <Add
                    onClick={() => {
                      const profile = { ...(row.profile ?? {}) };
                      let key = `milestone-${Object.keys(profile).length + 1}`;
                      while (profile[key] !== undefined) key += "-next";
                      update(i, { ...row, profile: { ...profile, [key]: 0 } });
                    }}
                  >
                    Add profile weight
                  </Add>
                </div>
              )}
              {currentMode === "recurring" && (
                <>
                  <label>
                    Recurring from
                    <input
                      aria-label="Recurring from"
                      type="date"
                      value={row.recurring?.start ?? ""}
                      onChange={(e) =>
                        update(i, {
                          ...row,
                          recurring: e.target.value
                            ? {
                                start: e.target.value,
                                end: row.recurring?.end ?? e.target.value,
                                amount: row.recurring?.amount ?? 0,
                                frequency:
                                  row.recurring?.frequency ?? "monthly",
                              }
                            : undefined,
                        })
                      }
                    />
                  </label>
                  <label>
                    Recurring to
                    <input
                      aria-label="Recurring to"
                      type="date"
                      value={row.recurring?.end ?? ""}
                      onChange={(e) =>
                        update(i, {
                          ...row,
                          recurring: row.recurring
                            ? { ...row.recurring, end: e.target.value }
                            : undefined,
                        })
                      }
                    />
                  </label>
                  <Num
                    label="Amount per occurrence"
                    value={row.recurring?.amount}
                    onChange={(amount) =>
                      update(i, {
                        ...row,
                        recurring: row.recurring
                          ? { ...row.recurring, amount }
                          : undefined,
                      })
                    }
                  />
                  <label>
                    Frequency
                    <select
                      aria-label="Frequency"
                      value={row.recurring?.frequency ?? "monthly"}
                      onChange={(e) =>
                        update(i, {
                          ...row,
                          recurring: row.recurring
                            ? {
                                ...row.recurring,
                                frequency: e.target.value as
                                  "daily" | "monthly" | "annual",
                              }
                            : undefined,
                        })
                      }
                    >
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </label>
                </>
              )}
              <Remove
                onClick={() => patch({ costs: rows.filter((_, j) => j !== i) })}
              />
            </div>
          );
        })}
      </>
    </Panel>
  );
}
function Facilities({
  finance,
  patch,
}: {
  finance: DatedFinanceInput;
  patch: (x: Partial<DatedFinanceInput>) => void;
}) {
  const rows = [...(finance.facilities ?? [])],
    contributions = [...(finance.contributions ?? [])],
    refinance = [...(finance.refinance ?? [])];
  const update = (i: number, x: Facility) =>
    patch({ facilities: rows.map((row, j) => (i === j ? x : row)) });
  return (
    <Panel
      title="Facilities, refinance and contributions"
      action={
        <Add
          onClick={() =>
            patch({
              facilities: [
                ...rows,
                {
                  id: id("facility"),
                  limit: 0,
                  annualRate: 0,
                  availabilityStart: finance.startDate,
                  availabilityEnd: finance.endDate,
                  equityMode: "equity-first",
                  interest: "paid",
                },
              ],
            })
          }
        >
          Add facility
        </Add>
      }
    >
      <div className="form-grid">
        <Num
          label="Equity budget"
          value={finance.equityBudget}
          onChange={(equityBudget) => patch({ equityBudget })}
        />
      </div>
      {rows.map((row, i) => (
        <div className="form-grid" key={row.id}>
          <label>
            Facility ID
            <input
              value={row.id}
              onChange={(e) => update(i, { ...row, id: e.target.value })}
            />
          </label>
          <Num
            label="Limit"
            value={row.limit}
            onChange={(limit) => update(i, { ...row, limit })}
          />
          <Num
            label="Annual rate (%)"
            value={row.annualRate}
            percent
            onChange={(annualRate) => update(i, { ...row, annualRate })}
          />
          <label>
            Availability start
            <input
              type="date"
              value={
                row.availability?.start ??
                row.availabilityStart ??
                row.open ??
                ""
              }
              onChange={(e) =>
                update(i, {
                  ...row,
                  availability: {
                    start: e.target.value,
                    end:
                      row.availability?.end ??
                      row.availabilityEnd ??
                      row.close ??
                      finance.endDate,
                  },
                  availabilityStart: e.target.value,
                  open: undefined,
                })
              }
            />
          </label>
          <label>
            Availability end
            <input
              type="date"
              value={
                row.availability?.end ?? row.availabilityEnd ?? row.close ?? ""
              }
              onChange={(e) =>
                update(i, {
                  ...row,
                  availability: {
                    start:
                      row.availability?.start ??
                      row.availabilityStart ??
                      row.open ??
                      finance.startDate,
                    end: e.target.value,
                  },
                  availabilityEnd: e.target.value,
                  close: undefined,
                })
              }
            />
          </label>
          <label>
            Maturity date
            <input
              type="date"
              value={
                row.maturityDate ??
                row.availability?.end ??
                row.availabilityEnd ??
                row.close ??
                ""
              }
              onChange={(e) =>
                update(i, { ...row, maturityDate: e.target.value || undefined })
              }
            />
          </label>
          <label>
            Mode
            <select
              value={row.equityMode ?? "equity-first"}
              onChange={(e) =>
                update(i, {
                  ...row,
                  equityMode: e.target.value as Facility["equityMode"],
                })
              }
            >
              <option value="equity-first">Equity first</option>
              <option value="pro-rata">Pro rata</option>
            </select>
          </label>
          <Num
            label="Funding share"
            value={row.fundingShare}
            onChange={(fundingShare) => update(i, { ...row, fundingShare })}
          />
          <label>
            Eligible categories
            <input
              value={(row.eligibleCategories ?? []).join(", ")}
              onChange={(e) =>
                update(i, {
                  ...row,
                  eligibleCategories: e.target.value
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>
          <label>
            Interest
            <select
              value={row.interest ?? "paid"}
              onChange={(e) =>
                update(i, {
                  ...row,
                  interest: e.target.value as Facility["interest"],
                })
              }
            >
              <option value="paid">Paid</option>
              <option value="capitalised">Capitalised</option>
            </select>
          </label>
          <Num
            label="Establishment fee"
            value={
              typeof row.establishmentFee === "number"
                ? row.establishmentFee
                : row.establishmentFee?.fixed
            }
            onChange={(fixed) =>
              update(i, {
                ...row,
                establishmentFee: {
                  fixed,
                  percent:
                    typeof row.establishmentFee === "object"
                      ? row.establishmentFee.percent
                      : undefined,
                },
              })
            }
          />
          <Num
            label="Establishment fee (%)"
            value={
              typeof row.establishmentFee === "object"
                ? row.establishmentFee.percent
                : undefined
            }
            percent
            onChange={(percent) =>
              update(i, {
                ...row,
                establishmentFee: {
                  fixed:
                    typeof row.establishmentFee === "object"
                      ? row.establishmentFee.fixed
                      : row.establishmentFee,
                  percent,
                },
              })
            }
          />
          <Num
            label="Recurring annual fee on limit (%)"
            value={row.recurringFees?.limit}
            percent
            onChange={(limit) =>
              update(i, {
                ...row,
                recurringFees: { ...row.recurringFees, limit },
              })
            }
          />
          <Num
            label="Recurring annual fee on drawn (%)"
            value={row.recurringFees?.drawn}
            percent
            onChange={(drawn) =>
              update(i, {
                ...row,
                recurringFees: { ...row.recurringFees, drawn },
              })
            }
          />
          <Num
            label="Recurring annual fee on undrawn (%)"
            value={row.recurringFees?.undrawn}
            percent
            onChange={(undrawn) =>
              update(i, {
                ...row,
                recurringFees: { ...row.recurringFees, undrawn },
              })
            }
          />
          <Remove
            onClick={() =>
              patch({ facilities: rows.filter((_, j) => j !== i) })
            }
          />
        </div>
      ))}
      <ContributionLines
        lines={contributions as Contribution[]}
        onChange={(next) => patch({ contributions: next })}
      />
      <Add
        onClick={() =>
          patch({ refinance: [...refinance, { date: finance.endDate }] })
        }
      >
        Add refinance
      </Add>
      {refinance.map((row, i) => (
        <div className="form-grid" key={i}>
          <label>
            Date
            <input
              type="date"
              value={row.date}
              onChange={(e) =>
                patch({
                  refinance: refinance.map((x, j) =>
                    j === i ? { ...x, date: e.target.value } : x,
                  ),
                })
              }
            />
          </label>
          <label>
            From facility
            <input
              value={row.fromFacility ?? ""}
              onChange={(e) =>
                patch({
                  refinance: refinance.map((x, j) =>
                    j === i
                      ? { ...x, fromFacility: e.target.value || undefined }
                      : x,
                  ),
                })
              }
            />
          </label>
          <label>
            To facility
            <input
              value={row.toFacility ?? ""}
              onChange={(e) =>
                patch({
                  refinance: refinance.map((x, j) =>
                    j === i
                      ? { ...x, toFacility: e.target.value || undefined }
                      : x,
                  ),
                })
              }
            />
          </label>
          <Num
            label="Principal"
            value={row.principal}
            onChange={(principal) =>
              patch({
                refinance: refinance.map((x, j) =>
                  j === i ? { ...x, principal } : x,
                ),
              })
            }
          />
          <Remove
            onClick={() =>
              patch({ refinance: refinance.filter((_, j) => j !== i) })
            }
          />
        </div>
      ))}
    </Panel>
  );
}
function Operating({
  finance,
  patch,
}: {
  finance: DatedFinanceInput;
  patch: (x: Partial<DatedFinanceInput>) => void;
}) {
  return (
    <Panel title="Opening, rent and operating cashflows">
      <div className="form-grid">
        <label>
          Opening date
          <input
            type="date"
            value={finance.openingDate ?? ""}
            onChange={(e) =>
              patch({ openingDate: e.target.value || undefined })
            }
          />
        </label>
        <label>
          Rent commencement
          <input
            type="date"
            value={finance.rentCommencementDate ?? ""}
            onChange={(e) =>
              patch({ rentCommencementDate: e.target.value || undefined })
            }
          />
        </label>
        <label>
          Terminal date
          <input
            type="date"
            value={finance.terminalProceeds?.date ?? ""}
            onChange={(e) =>
              patch({
                terminalProceeds: e.target.value
                  ? {
                      date: e.target.value,
                      amount: finance.terminalProceeds?.amount ?? 0,
                    }
                  : undefined,
              })
            }
          />
        </label>
        <Num
          label="Terminal proceeds"
          value={finance.terminalProceeds?.amount}
          onChange={(amount) =>
            patch({
              terminalProceeds: finance.terminalProceeds
                ? { ...finance.terminalProceeds, amount }
                : undefined,
            })
          }
        />
      </div>
      <Rent
        label="Landlord rent"
        rows={finance.landlordRent ?? []}
        set={(landlordRent) => patch({ landlordRent })}
      />
      <Rent
        label="Landlord expenses"
        rows={finance.landlordExpenses ?? []}
        set={(landlordExpenses) => patch({ landlordExpenses })}
      />
      <Rent
        label="Operator income"
        rows={finance.operatorRevenue ?? []}
        set={(operatorRevenue) => patch({ operatorRevenue })}
      />
      <Rent
        label="Operator expenses"
        rows={finance.operatorExpenses ?? []}
        set={(operatorExpenses) => patch({ operatorExpenses })}
      />
    </Panel>
  );
}
function Rent({
  label,
  rows,
  set,
}: {
  label: string;
  rows: readonly RentLine[];
  set: (next: RentLine[]) => void;
}) {
  return (
    <div className="well">
      <div className="panel-heading">
        <h4>{label}</h4>
        <Add onClick={() => set([...rows, { date: today(), amount: 0 }])}>
          Add row
        </Add>
      </div>
      <Schedule label={label} onCreate={(next) => set([...rows, ...next])} />
      {rows.map((row, i) => (
        <div className="form-grid" key={i}>
          <label>
            Date
            <input
              type="date"
              value={row.date ?? ""}
              onChange={(e) =>
                set(
                  rows.map((x, j) =>
                    j === i ? { ...x, date: e.target.value } : x,
                  ),
                )
              }
            />
          </label>
          <label>
            Milestone
            <input
              value={row.milestone ?? ""}
              onChange={(e) =>
                set(
                  rows.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          milestone: e.target.value || undefined,
                          date: e.target.value ? undefined : x.date,
                        }
                      : x,
                  ),
                )
              }
            />
          </label>
          <Num
            label="Days after milestone"
            value={row.days ?? 0}
            onChange={(days) =>
              set(rows.map((x, j) => (j === i ? { ...x, days } : x)))
            }
          />
          <Num
            label={`${label} amount`}
            value={row.amount}
            onChange={(amount) =>
              set(rows.map((x, j) => (j === i ? { ...x, amount } : x)))
            }
          />
          <label>
            Category
            <input
              value={row.category ?? ""}
              onChange={(e) =>
                set(
                  rows.map((x, j) =>
                    j === i
                      ? { ...x, category: e.target.value || undefined }
                      : x,
                  ),
                )
              }
            />
          </label>
          <Remove onClick={() => set(rows.filter((_, j) => j !== i))} />
        </div>
      ))}
    </div>
  );
}
function Schedule({
  label,
  onCreate,
}: {
  label: string;
  onCreate: (lines: RentLine[]) => void;
}) {
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [amount, setAmount] = useState(0);
  const [frequency, setFrequency] = useState<"monthly" | "annual">("annual");
  const [escalation, setEscalation] = useState(0);
  const create = () => {
    if (
      !start ||
      !end ||
      end < start ||
      !Number.isFinite(amount) ||
      !Number.isFinite(escalation)
    )
      return;
    const current = new Date(`${start}T00:00:00Z`);
    const until = new Date(`${end}T00:00:00Z`);
    const firstYear = current.getUTCFullYear();
    const lines: RentLine[] = [];
    while (current <= until) {
      const years = current.getUTCFullYear() - firstYear;
      lines.push({
        date: current.toISOString().slice(0, 10),
        amount: amount * (1 + escalation) ** years,
        category: `${label} generated ${frequency} schedule; ${escalation === 0 ? "no escalation" : `${(escalation * 100).toFixed(2)}% annual escalation`}`,
      });
      if (frequency === "monthly")
        current.setUTCMonth(current.getUTCMonth() + 1);
      else current.setUTCFullYear(current.getUTCFullYear() + 1);
    }
    onCreate(lines);
  };
  return (
    <details>
      <summary>Generate recurring payments</summary>
      <div className="form-grid">
        <label>
          Start
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label>
          End
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
        <Num label="Payment amount" value={amount} onChange={setAmount} />
        <label>
          Frequency
          <select
            value={frequency}
            onChange={(e) =>
              setFrequency(e.target.value as "monthly" | "annual")
            }
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </label>
        <Num
          label="Annual escalation (%)"
          value={escalation}
          percent
          onChange={setEscalation}
        />
        <Add onClick={create}>Generate editable rows</Add>
      </div>
    </details>
  );
}
function Ownership({
  ownership,
  landlordRent,
  derivedLease,
  patch,
}: {
  ownership: OwnershipInput;
  landlordRent: RentLine[];
  derivedLease: boolean;
  patch: (x: Partial<OwnershipInput>) => void;
}) {
  const lifecycle = ownership.ownerLifecycle,
    categories = lifecycle?.categories ?? [],
    finance = ownership.financing;
  return (
    <Panel title="Ownership lifecycle and financing">
      <div className="form-grid">
        <label>
          Valuation date
          <input
            type="date"
            value={ownership.valuationDate}
            onChange={(e) => patch({ valuationDate: e.target.value })}
          />
        </label>
        <label>
          End date
          <input
            type="date"
            value={ownership.endDate}
            onChange={(e) => patch({ endDate: e.target.value })}
          />
        </label>
        <Num
          label="Ownership discount rate (%)"
          value={ownership.annualDiscountRate}
          percent
          onChange={(annualDiscountRate) => patch({ annualDiscountRate })}
        />
        <Num
          label="Terminal value"
          value={ownership.terminalValue}
          onChange={(terminalValue) => patch({ terminalValue })}
        />
        <Num
          label="Selling costs"
          value={ownership.sellingCosts}
          onChange={(sellingCosts) => patch({ sellingCosts })}
        />
      </div>
      <p className="muted">
        {derivedLease
          ? `Lease payments derive from ${landlordRent.length} dated landlord-rent row(s) until an explicit override is added.`
          : "Lease payments below are an explicit override."}
      </p>
      <Lines
        label="Lease payment override"
        lines={ownership.leasePayments}
        onChange={(leasePayments) => patch({ leasePayments })}
      />
      <Lines
        label="Owner acquisition and build costs"
        lines={ownership.ownerAcquisitionAndBuildCosts}
        onChange={(ownerAcquisitionAndBuildCosts) =>
          patch({ ownerAcquisitionAndBuildCosts })
        }
      />
      <label>
        <input
          type="checkbox"
          checked={lifecycle?.declaredComplete ?? false}
          onChange={(e) =>
            patch({
              ownerLifecycle: {
                declaredComplete: e.target.checked,
                categories,
              },
            })
          }
        />{" "}
        All lifecycle categories are complete
      </label>
      <Add
        onClick={() =>
          patch({
            ownerLifecycle: {
              declaredComplete: lifecycle?.declaredComplete ?? false,
              categories: [
                ...categories,
                { name: "", annualAmount: null, escalationRate: 0 },
              ],
            },
          })
        }
      >
        Add lifecycle category
      </Add>
      {categories.map((row, i) => (
        <div className="form-grid" key={i}>
          <label>
            Category
            <input
              value={row.name}
              onChange={(e) =>
                patch({
                  ownerLifecycle: {
                    declaredComplete: lifecycle?.declaredComplete ?? false,
                    categories: categories.map((x, j) =>
                      j === i ? { ...x, name: e.target.value } : x,
                    ),
                  },
                })
              }
            />
          </label>
          <Num
            label="Annual amount"
            value={row.annualAmount}
            onChange={(annualAmount) =>
              patch({
                ownerLifecycle: {
                  declaredComplete: lifecycle?.declaredComplete ?? false,
                  categories: categories.map((x, j) =>
                    j === i ? { ...x, annualAmount } : x,
                  ),
                },
              })
            }
          />
          <Num
            label="Escalation (%)"
            value={row.escalationRate}
            percent
            onChange={(escalationRate) =>
              patch({
                ownerLifecycle: {
                  declaredComplete: lifecycle?.declaredComplete ?? false,
                  categories: categories.map((x, j) =>
                    j === i ? { ...x, escalationRate } : x,
                  ),
                },
              })
            }
          />
          <label>
            First payment
            <input
              type="date"
              value={row.firstPaymentDate ?? ""}
              onChange={(e) =>
                patch({
                  ownerLifecycle: {
                    declaredComplete: lifecycle?.declaredComplete ?? false,
                    categories: categories.map((x, j) =>
                      j === i
                        ? {
                            ...x,
                            firstPaymentDate: e.target.value || undefined,
                          }
                        : x,
                    ),
                  },
                })
              }
            />
          </label>
          <Remove
            onClick={() =>
              patch({
                ownerLifecycle: {
                  declaredComplete: lifecycle?.declaredComplete ?? false,
                  categories: categories.filter((_, j) => j !== i),
                },
              })
            }
          />
        </div>
      ))}
      <label>
        <input
          type="checkbox"
          checked={Boolean(finance)}
          onChange={(e) =>
            patch({
              financing: e.target.checked
                ? (finance ?? {
                    draws: [],
                    repayments: [],
                    interestPayments: [],
                    fees: [],
                  })
                : null,
            })
          }
        />{" "}
        Include owner financing
      </label>
      {finance && (
        <>
          <Lines
            label="Financing draws"
            lines={finance.draws}
            onChange={(draws) => patch({ financing: { ...finance, draws } })}
          />
          <Lines
            label="Financing repayments"
            lines={finance.repayments}
            onChange={(repayments) =>
              patch({ financing: { ...finance, repayments } })
            }
          />
          <Lines
            label="Financing interest"
            lines={finance.interestPayments}
            onChange={(interestPayments) =>
              patch({ financing: { ...finance, interestPayments } })
            }
          />
          <Lines
            label="Financing fees"
            lines={finance.fees}
            onChange={(fees) => patch({ financing: { ...finance, fees } })}
          />
        </>
      )}
    </Panel>
  );
}
export default DealWorkspace;
